import React, { useState, useEffect } from 'react';
import { useFetch, useScrollRestore } from '../../hooks/useFetch';
import { showGlobalToast, Spinner, ErrorBanner, Badge, ImportButton, ImportResult, EmptyState, Modal, SearchableSelect } from '../../components/ui';
import { fmt, daysOfCoverClass, errMsg } from '../../utils/format';
import api, { downloadFile } from '../../utils/api';
import AddProductModal from './AddProduct';
import EditProductModal from './EditProduct';
import ProductAdjustmentLogModal from './ProductAdjustmentLog';
import { useAuth } from '../../context/AuthContext';

import { useNavigate, useSearchParams } from 'react-router-dom';

function AlertThresholdModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    alertsEnabled: product.alerts_enabled !== false,
    lowStockThreshold: product.low_stock_threshold ?? 0,
    lowStockSource: product.low_stock_source ?? 'total',
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/api/products/${product.id}`, form);
      onSaved();
      onClose();
    } catch (err) {
      alert(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Alert thresholds — ${product.name}`} onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="fg-alert-form" type="submit">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }>
      <form id="fg-alert-form" onSubmit={save}>
        <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="checkbox" id="alerts-enabled" style={{ width:'auto' }}
            checked={form.alertsEnabled}
            onChange={e => setForm(f => ({ ...f, alertsEnabled: e.target.checked }))} />
          <label htmlFor="alerts-enabled" style={{ marginBottom:0, cursor:'pointer' }}>
            Alerts enabled for this product
          </label>
        </div>
        <small style={{ fontSize:11, color:'var(--text-tertiary)', display:'block', marginBottom:12 }}>
          When enabled, an out-of-stock alert fires automatically when quantity reaches 0, even if no threshold is set.
        </small>
        <div className="form-group">
          <label>Low stock threshold (units)</label>
          <input type="number" min="0" value={form.lowStockThreshold}
            onChange={e => setForm(f => ({ ...f, lowStockThreshold: parseInt(e.target.value) || 0 }))} />
          <small style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            Alert fires when quantity is at or below this number. Set to 0 for out-of-stock only.
          </small>
        </div>
        <div className="form-group">
          <label>Quantity to monitor</label>
          <select value={form.lowStockSource}
            onChange={e => setForm(f => ({ ...f, lowStockSource: e.target.value }))}>
            <option value="total">Total (warehouse + Amazon FBA)</option>
            <option value="warehouse">Warehouse only</option>
          </select>
        </div>
      </form>
    </Modal>
  );
}


function ConvertToKitModal({ product, onClose, onSaved }) {
  const { data: products } = useFetch('/api/products');
  const { data: kitData } = useFetch(product.kit_id ? `/api/kits/${product.kit_id}` : null);
  const [components, setComponents] = useState([{ productId: '', quantity: '1' }]);
  const [sku, setSku] = useState(product.sku || '');
  const [saving, setSaving] = useState(false);
  const isExistingKit = !!product.kit_id;

  // Load existing components when editing an existing kit
  React.useEffect(() => {
    if (kitData?.components?.length) {
      setComponents(kitData.components.map(c => ({
        productId: String(c.product_id ?? c.productId),
        quantity: String(c.quantity),
      })));
    }
  }, [kitData]);

  const addComponent = () => setComponents(c => [...c, { productId: '', quantity: '1' }]);
  const removeComponent = (idx) => setComponents(c => c.filter((_, j) => j !== idx));
  const setComp = (idx, k, v) => setComponents(c => c.map((row, j) => j === idx ? { ...row, [k]: v } : row));

  const save = async (e) => {
    e.preventDefault();
    const valid = components.filter(c => c.productId && c.quantity);
    if (!valid.length) { alert('Add at least one component'); return; }
    if (valid.find(c => parseInt(c.productId) === product.id)) { alert('A kit cannot contain itself'); return; }
    setSaving(true);
    try {
      await api.post('/api/kits/from-product', {
        productId: product.id,
        sku: sku || undefined,
        components: valid.map(c => ({ productId: parseInt(c.productId), quantity: parseInt(c.quantity) }))
      });
      onSaved(); onClose();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const removeKit = async () => {
    if (!window.confirm('Remove the kit definition? The product stays as a finished good.')) return;
    try { await api.delete('/api/kits/from-product/' + product.id); onSaved(); onClose(); }
    catch (err) { alert(errMsg(err)); }
  };

  const available = (products || []).filter(p => p.id !== product.id);
  const productOptions = available.map(p => ({ value: p.id, label: `${p.sku} — ${p.name} (on hand: ${p.quantity_on_hand})` }));

  return (
    <Modal title={(isExistingKit ? 'Edit' : 'Convert to') + ' Kit — ' + product.name} onClose={onClose} wide
      footer={
        <div style={{ display:'flex', justifyContent:'space-between', width:'100%' }}>
          <div>{isExistingKit && <button className="btn btn-danger btn-sm" onClick={removeKit}><i className="ti ti-unlink" /> Remove kit</button>}</div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" form="kit-convert-form" type="submit">{saving?'Saving…':isExistingKit?'Update kit':'Convert to kit'}</button>
          </div>
        </div>
      }>
      <div className="alert alert-info" style={{ marginBottom:16 }}>
        <i className="ti ti-info-circle" />
        <div style={{ fontSize:12 }}><strong>{product.name}</strong> stays in Finished Goods and also appears in Bundles. Its buildable qty is calculated from the components below.</div>
      </div>
      <form id="kit-convert-form" onSubmit={save}>
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label>Kit SKU</label>
          <input value={sku} onChange={e => setSku(e.target.value)} placeholder="Leave blank to keep existing" />
        </div>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>Components</div>
        {components.map((comp, idx) => (
          <div key={idx} style={{ display:'grid', gridTemplateColumns:'3fr 1fr auto', gap:8, marginBottom:8, alignItems:'end' }}>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Component product</label>}
              <SearchableSelect
                value={comp.productId}
                onChange={v => setComp(idx,'productId',v)}
                options={productOptions}
                placeholder="— Search or select —"
              />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              {idx===0 && <label style={{ fontSize:11 }}>Qty per kit</label>}
              <input type="number" min="1" value={comp.quantity} onChange={e => setComp(idx,'quantity',e.target.value)} />
            </div>
            <button type="button" className="btn btn-sm btn-danger" style={{ marginTop:idx===0?18:0 }} onClick={() => removeComponent(idx)}>
              <i className="ti ti-x" />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={addComponent} style={{ marginTop:4 }}>
          <i className="ti ti-plus" /> Add component
        </button>
      </form>
    </Modal>
  );
}

function ProductRow({ p, indent, isManager, setEditModal, setKitModal, setAlertModal, setLogModal, getAlertStatus, daysOfCoverClass, fmt, onWarehouseEdit, saveScroll, onSavedScrollRef, lastEditedId }) {
  const alertStatus = getAlertStatus(p);
  const [editingQty, setEditingQty] = useState(false);
  const [qtyVal, setQtyVal] = useState('');
  const [prevQty, setPrevQty] = useState(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setQtyVal(String(p.quantity_on_hand));
    setPrevQty(p.quantity_on_hand);
    setEditingQty(true);
  };

  const commitEdit = async (e) => {
    e.stopPropagation();
    setEditingQty(false);
    const newQty = parseFloat(qtyVal);
    if (!isNaN(newQty) && newQty !== parseFloat(p.quantity_on_hand)) {
      await onWarehouseEdit(p.id, newQty, prevQty);
    }
  };

  return (
    <tr onClick={() => {
      const el = document.querySelector('.page-body');
      if (el && onSavedScrollRef) onSavedScrollRef.current = el.scrollTop;
      saveScroll();
      setEditModal(p);
      if (el) el.scrollTop = 0;
    }} style={{ cursor: 'pointer', ...(lastEditedId === p.id ? { background: 'var(--blue-light, #e8f0fe)', outline: '2px solid var(--blue)', outlineOffset: '-2px', transition: 'background 0.5s' } : alertStatus ? { background: alertStatus === 'out_of_stock' ? 'var(--red-light)' : 'var(--amber-light)' } : {}) }}>
      <td></td>
      <td style={{ fontWeight: indent ? 400 : 500, paddingLeft: indent ? 16 : undefined }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {p.image_url
            ? <img src={p.image_url} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:4, flexShrink:0 }} />
            : <div style={{ width:36, height:36, borderRadius:4, background:'var(--surface-2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className="ti ti-package" style={{ fontSize:14, color:'var(--text-tertiary)' }} />
              </div>}
          {p.name}
        </div>
      </td>
      <td onClick={isManager ? startEdit : undefined} style={{ cursor: isManager ? 'text' : undefined }}>
        {editingQty ? (
          <input
            autoFocus
            type="number"
            value={qtyVal}
            style={{ width: 70, fontSize: 12, padding: '2px 4px' }}
            onClick={e => e.stopPropagation()}
            onChange={e => setQtyVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(e); if (e.key === 'Escape') setEditingQty(false); }}
          />
        ) : (
          <span title={isManager ? 'Click to edit' : undefined}>{fmt.number(p.quantity_on_hand)}</span>
        )}
      </td>
      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku}</td>
      <td>{fmt.currency(p.unit_price)}</td>
      <td>
        {p.kit_id
          ? <span className="badge badge-blue">Bundle &middot; {p.kit_buildable_qty ?? 0} buildable</span>
          : <span className="text-muted" style={{ fontSize:11 }}>—</span>}
      </td>
      <td>
        {alertStatus === 'out_of_stock' && <span className="badge badge-crit">Out of stock</span>}
        {alertStatus === 'low_stock' && <span className="badge badge-amber">Low stock</span>}
        {!alertStatus && p.low_stock_threshold > 0 && <span className="badge badge-ok">OK</span>}
        {!p.low_stock_threshold && <span className="text-muted" style={{ fontSize: 11 }}>No alert</span>}
      </td>
    </tr>
  );
}

export default function Products() {
  const { data, loading, error, refetch } = useFetch('/api/products');
  const { isManager } = useAuth();
  const { saveScroll, restoreScroll, applyScroll } = useScrollRestore('sf_products_scroll');
  const savedScrollRef = React.useRef(0);
  const [lastEditedId, setLastEditedId] = React.useState(null);

  const restoreScrollPos = React.useCallback(() => {
    const pos = savedScrollRef.current;
    const attempt = (tries) => {
      const el = document.querySelector('.page-body');
      if (el) { el.scrollTop = pos; return; }
      if (tries > 0) setTimeout(() => attempt(tries - 1), 50);
    };
    setTimeout(() => attempt(5), 50);
  }, []);
  const [search, setSearch] = useState(() => sessionStorage.getItem('sf_products_search') || '');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [kitModal, setKitModal] = useState(null);
  const [logModal, setLogModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { applyScroll(); }, [data]); // eslint-disable-line

  // If we arrived back here with ?reopen=PRODUCT_ID (e.g. after editing a BOM),
  // reopen that product's edit modal automatically.
  useEffect(() => {
    const reopenId = searchParams.get('reopen');
    if (!reopenId || !data) return;
    const product = data.find(p => String(p.id) === String(reopenId));
    if (product) setEditModal(product);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data]);

  // Restore scroll position once the product list itself has rendered
  // (after a return trip from BOM/Kit editing). Handled by useScrollRestore hook.

  const handleImport = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: res } = await api.post('/api/products/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res);
      refetch();
    } catch (err) {
      setImportResult({ error: errMsg(err) });
    } finally {
      setImporting(false);
    }
  };

  const handleWarehouseEdit = async (productId, newQty, prevQty) => {
    try {
      await api.patch(`/api/products/${productId}`, { quantityOnHand: newQty, prevQuantity: prevQty });
      refetch();
    } catch (err) { alert(errMsg(err)); }
  };

  const [vendorFeedOnly, setVendorFeedOnly] = useState(false);

  const filtered = (data || []).filter(p => {
    if (vendorFeedOnly && !p.include_in_vendor_feed) return false;
    return !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.sku.toLowerCase().includes(search.toLowerCase());
  });

  // Group products into collapsible rows by shared base name — the text
  // before the first parenthetical variant suffix. For example, all
  // FFS Carbon Fiber Transducer Pole Combo variants group under one row.
  const baseName = (name) => {
    const idx = name.indexOf('(');
    return idx > 0 ? name.slice(0, idx).trim() : name.trim();
  };

  const groups = React.useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const key = `name:${baseName(p.name)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    // Flatten into a list of { isGroup, label, items } — singletons render as plain rows
    const result = [];
    for (const [key, items] of map) {
      if (items.length > 1) {
        result.push({ isGroup: true, key, label: baseName(items[0].name), items, isKit: items.some(p => p.kit_id) });
      } else {
        result.push({ isGroup: false, key, items });
      }
    }
    // Keep original relative ordering by first item's position in filtered
    const order = new Map(filtered.map((p, i) => [p.id, i]));
    result.sort((a, b) => order.get(a.items[0].id) - order.get(b.items[0].id));
    return result;
  }, [filtered]);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('sf_products_expanded') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (key) => setExpandedGroups(e => {
    const next = { ...e, [key]: !e[key] };
    sessionStorage.setItem('sf_products_expanded', JSON.stringify(next));
    return next;
  });

  const lowStockCount = (data || []).filter(p => {
    if (!p.low_stock_threshold) return false;
    const qty = p.low_stock_source === 'warehouse' ? p.quantity_on_hand : p.quantity_total;
    return qty > 0 && qty <= p.low_stock_threshold;
  }).length;
  const outOfStockCount = (data || []).filter(p => {
    if (!p.low_stock_threshold) return false;
    const qty = p.low_stock_source === 'warehouse' ? p.quantity_on_hand : p.quantity_total;
    return qty === 0;
  }).length;

  const getAlertStatus = (p) => {
    if (!p.low_stock_threshold) return null;
    const qty = p.low_stock_source === 'warehouse' ? p.quantity_on_hand : p.quantity_total;
    if (qty === 0) return 'out_of_stock';
    if (qty <= p.low_stock_threshold) return 'low_stock';
    return null;
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      {editModal && editModal._new && (
        <AddProductModal
          onClose={() => { setEditModal(null); restoreScroll(); }}
          onSaved={() => { refetch(); showGlobalToast('Product added successfully'); }}
        />
      )}
      {kitModal && (
        <ConvertToKitModal
          product={kitModal}
          onClose={() => setKitModal(null)}
          onSaved={() => { refetch(); showGlobalToast('Kit definition saved successfully'); }}
        />
      )}
      {alertModal && (
        <AlertThresholdModal
          product={alertModal}
          onClose={() => setAlertModal(null)}
          onSaved={() => { refetch(); showGlobalToast('Alert threshold updated'); }}
        />
      )}

      {logModal && (
        <ProductAdjustmentLogModal product={logModal} onClose={() => setLogModal(null)} />
      )}

      {editModal && !editModal._new && (
        <EditProductModal
          product={editModal}
          inline={true}
          onClose={() => {
            const id = editModal?.id;
            setEditModal(null);
            setLastEditedId(id);
            setTimeout(() => setLastEditedId(null), 2000);
            restoreScrollPos();
          }}
          onSaved={async () => {
            const id = editModal?.id;
            setEditModal(null);
            showGlobalToast('Product updated successfully');
            setLastEditedId(id);
            setTimeout(() => setLastEditedId(null), 2000);
            await refetch();
            restoreScrollPos();
          }}
          onOpenKit={(p) => setKitModal(p)}
          onOpenAlert={(p) => setAlertModal(p)}
          onOpenLog={(p) => setLogModal(p)}
        />
      )}
      {!editModal && (<div><div className="page-header">
        <div>
          <h1>Finished Goods</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => {
              const allCollapsed = {};
              sessionStorage.setItem('sf_products_expanded', JSON.stringify(allCollapsed));
              setExpandedGroups(allCollapsed);
            }}>
              <i className="ti ti-fold" /> Collapse all
            </button>
            <button className="btn btn-sm" onClick={() => downloadFile('/api/products/import/template')}>
              <i className="ti ti-download" /> Template
            </button>
            <ImportButton onFile={handleImport} />
            <button className="btn btn-sm" onClick={() => downloadFile('/api/products/export/csv')}>
              <i className="ti ti-download" /> Export CSV
            </button>
            {isManager && (
              <button className="btn btn-primary btn-sm" onClick={() => setEditModal({ _new: true })}>
                <i className="ti ti-plus" /> Add SKU
              </button>
            )}
          </div>
        </div>
      </div>

      {outOfStockCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 10 }}>
          <i className="ti ti-alert-circle" />
          <strong>{outOfStockCount} SKU{outOfStockCount > 1 ? 's' : ''} out of stock</strong>
          &nbsp;— immediate action needed.
        </div>
      )}
      {lowStockCount > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" />
          <strong>{lowStockCount} SKU{lowStockCount > 1 ? 's' : ''} below low-stock threshold.</strong>
        </div>
      )}

      {importing && <div className="alert alert-info"><i className="ti ti-loader" /> Importing…</div>}
      {importResult && <ImportResult result={importResult} />}

      <div className="filter-bar">
        <div className="search-input">
          <i className="ti ti-search" />
          <input placeholder="Search SKU or name…" value={search}
            onChange={e => { setSearch(e.target.value); sessionStorage.setItem('sf_products_search', e.target.value); }} />
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
          <input type="checkbox" style={{ width:'auto' }} checked={vendorFeedOnly}
            onChange={e => setVendorFeedOnly(e.target.checked)} />
          Vendor feed only
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Product</th>
                <th>Warehouse</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Bundle</th>
                <th>Alert</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                if (!g.isGroup) {
                  const p = g.items[0];
                  return (
                    <ProductRow key={p.id} p={p} isManager={isManager}
                      setEditModal={setEditModal} setKitModal={setKitModal}
                      setAlertModal={setAlertModal} setLogModal={setLogModal}
                      getAlertStatus={getAlertStatus} daysOfCoverClass={daysOfCoverClass} fmt={fmt}
                      onWarehouseEdit={handleWarehouseEdit} saveScroll={saveScroll} onSavedScrollRef={savedScrollRef} lastEditedId={lastEditedId} />
                  );
                }
                const expanded = !!expandedGroups[g.key];
                const totalOnHand = g.items.reduce((s, p) => s + (parseFloat(p.quantity_on_hand) || 0), 0);
                const totalAmazon = g.items.reduce((s, p) => s + (parseFloat(p.quantity_at_amazon) || 0), 0);
                const anyAlert = g.items.some(p => getAlertStatus(p));
                const anyOut = g.items.some(p => getAlertStatus(p) === 'out_of_stock');
                return (
                  <React.Fragment key={g.key}>
                    <tr onClick={() => toggleGroup(g.key)}
                      style={{ cursor: 'pointer', background: 'var(--bg-secondary)', ...(anyOut ? { background: 'var(--red-light)' } : anyAlert ? { background: 'var(--amber-light)' } : {}) }}>
                      <td>
                        <i className={`ti ${expanded ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 12, color: 'var(--text-tertiary)' }} />
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {g.label}
                        {g.isKit && <span className="badge badge-blue" style={{ marginLeft: 8, fontSize: 10 }}>Bundle</span>}
                      </td>
                      <td>{fmt.number(totalOnHand)}</td>
                      <td>{fmt.number(totalAmazon)}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {g.items.length} variant{g.items.length !== 1 ? 's' : ''}
                      </td>
                      <td colSpan={5}></td>
                    </tr>
                    {expanded && g.items.map(p => (
                      <ProductRow key={p.id} p={p} indent isManager={isManager}
                        setEditModal={setEditModal} setKitModal={setKitModal}
                        setAlertModal={setAlertModal} setLogModal={setLogModal}
                        getAlertStatus={getAlertStatus} daysOfCoverClass={daysOfCoverClass} fmt={fmt}
                        onWarehouseEdit={handleWarehouseEdit} saveScroll={saveScroll} onSavedScrollRef={savedScrollRef} lastEditedId={lastEditedId} />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <EmptyState icon="ti-package" message="No products found" />}
        </div>
      </div>
      </div>)}{/* end !editModal */}
    </div>
  );
}